import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { checkAppVersion, startVersionCheckInterval } from '../services/versionService';

/**
 * Custom hook to handle version checking on route changes and at intervals
 */
export function useVersionHandler() {
  const location = useLocation();
  const previousPath = useRef(location.pathname);
  const hasInitialized = useRef(false);
  
  const FADE_DURATION_MS = 250;
  const DELAY_BEFORE_FADE_MS = 700;

  // Single place to hide overlay: fade out then remove (avoids flicker, lets app paint first)
  useEffect(() => {
    const updateInProgress = localStorage.getItem('update_in_progress');
    if (!updateInProgress) return;

    const overlay = document.getElementById('version-update-overlay');
    const updateStartTime = parseInt(updateInProgress, 10);
    const currentTime = Date.now();

    if (isNaN(updateStartTime) || currentTime - updateStartTime > 30000) {
      if (window.hideVersionUpdateOverlay) {
        window.hideVersionUpdateOverlay();
      } else if (overlay) {
        overlay.style.display = 'none';
        localStorage.removeItem('update_in_progress');
        document.documentElement.classList.remove('show-version-overlay');
      }
      return;
    }

    if (!overlay) return;

    const hideWithFade = () => {
      overlay.style.opacity = '0';
      overlay.style.transition = 'opacity 0.25s ease';
      setTimeout(() => {
        if (window.hideVersionUpdateOverlay) {
          window.hideVersionUpdateOverlay();
        } else {
          overlay.style.display = 'none';
          overlay.style.opacity = '';
          localStorage.removeItem('update_in_progress');
          document.documentElement.classList.remove('show-version-overlay');
        }
      }, FADE_DURATION_MS);
    };

    const t = setTimeout(hideWithFade, DELAY_BEFORE_FADE_MS);
    return () => clearTimeout(t);
  }, []);
  
  // One-time initialization check
  useEffect(() => {
    if (!hasInitialized.current) {
      
      // Initial check on mount with force flag
      checkAppVersion(true);
      
      // Start interval checking
      const stopInterval = startVersionCheckInterval();
      hasInitialized.current = true;
      
      // Clean up interval on unmount
      return () => {
        stopInterval();
      };
    }
  }, []);
  
  // Check version on route changes
  useEffect(() => {
    if (location.pathname !== previousPath.current) {
      // Only check when path actually changes
      checkAppVersion();
      previousPath.current = location.pathname;
    }
  }, [location.pathname]);
  
  // Add additional check for user activity
  useEffect(() => {
    let activityTimeout;
    
    const handleUserActivity = () => {
      clearTimeout(activityTimeout);
      activityTimeout = setTimeout(() => {
        checkAppVersion();
      }, 60000); // Check after 1 minute of inactivity
    };
    
    // Add activity listeners
    window.addEventListener('click', handleUserActivity);
    window.addEventListener('keypress', handleUserActivity);
    
    return () => {
      clearTimeout(activityTimeout);
      window.removeEventListener('click', handleUserActivity);
      window.removeEventListener('keypress', handleUserActivity);
    };
  }, []);
}